import React, { useState } from 'react';

import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import WhatsAppButton from 'components/ui/WhatsAppButton';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import Button from 'components/ui/Button';
import Input from 'components/ui/Input';
import { trackFormSubmission, trackLeadGeneration } from 'utils/analytics';

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    // Track form submission
    trackFormSubmission('contact_form', {
      user_name: formData?.name,
      user_email: formData?.email,
      user_phone: formData?.phone,
      user_company: formData?.company,
      message_subject: formData?.subject
    });

    // Track lead generation
    trackLeadGeneration('contact', {
      email: formData?.email,
      company: formData?.company,
      name: formData?.name
    });

    console.log('Form submitted:', formData);
    // Handle form submission logic here
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <WhatsAppButton />
      
      {/* Hero Section - Dark with CRF Branding */}
      <section className="relative overflow-hidden pt-32 pb-20 bg-gradient-to-br from-gray-900 via-[#1a0a0d] to-black">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 rounded-full" style={{background: 'radial-gradient(circle, #96121D 0%, transparent 70%)'}}></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full" style={{background: 'radial-gradient(circle, #9B8E57 0%, transparent 70%)'}}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6 border" style={{backgroundColor: 'rgba(150, 18, 29, 0.1)', borderColor: 'rgba(150, 18, 29, 0.3)'}}>
              <Icon name="MessageSquare" size={18} style={{color: '#9B8E57'}} />
              <span className="text-sm font-semibold" style={{color: '#9B8E57'}}>Contactez-nous</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Parlons de Votre{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r" style={{backgroundImage: 'linear-gradient(to right, #96121D, #9B8E57)'}}>
                Projet
              </span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Notre équipe d'experts est à votre disposition pour vous accompagner dans vos projets de formation, conseil stratégique et transformation organisationnelle.
            </p>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl font-bold mb-1" style={{color: '#9B8E57'}}>2h</div>
                <div className="text-sm text-gray-400">Réponse urgente</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl font-bold mb-1" style={{color: '#9B8E57'}}>24h</div>
                <div className="text-sm text-gray-400">Réponse standard</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl font-bold mb-1" style={{color: '#9B8E57'}}>95%</div>
                <div className="text-sm text-gray-400">Taux satisfaction</div>
              </div>
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
                <div className="text-3xl font-bold mb-1" style={{color: '#9B8E57'}}>24/7</div>
                <div className="text-sm text-gray-400">Support disponible</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Methods Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Plusieurs Moyens de Nous Contacter
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choisissez le canal qui vous convient le mieux
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Phone */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 group">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: 'rgba(150, 18, 29, 0.1)'}}>
                <Icon name="Phone" size={24} style={{color: '#96121D'}} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Téléphone</h3>
              <p className="text-sm text-gray-600 mb-4">Appelez-nous directement</p>
              <a href="tel:+12409784155" className="font-semibold hover:underline" style={{color: '#96121D'}}>
                +1 (240) 978-4155
              </a>
            </div>

            {/* WhatsApp */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 group">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: 'rgba(155, 142, 87, 0.1)'}}>
                <Icon name="MessageCircle" size={24} style={{color: '#9B8E57'}} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-sm text-gray-600 mb-4">Chat instantané</p>
              <a href="https://wa.me/12409784155" target="_blank" rel="noopener noreferrer" className="font-semibold hover:underline" style={{color: '#9B8E57'}}>
                +1 (240) 978-4155
              </a>
            </div>

            {/* Email */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 group">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: 'rgba(150, 18, 29, 0.1)'}}>
                <Icon name="Mail" size={24} style={{color: '#96121D'}} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Email</h3>
              <p className="text-sm text-gray-600 mb-4">Écrivez-nous</p>
              <a href="mailto:contact@crfperfection.pro" className="font-semibold hover:underline" style={{color: '#96121D'}}>
                contact@crfperfection.pro
              </a>
            </div>

            {/* Location */}
            <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl p-6 border border-gray-200 hover:shadow-xl transition-all duration-300 group">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300" style={{backgroundColor: 'rgba(155, 142, 87, 0.1)'}}>
                <Icon name="MapPin" size={24} style={{color: '#9B8E57'}} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Adresse</h3>
              <p className="text-sm text-gray-600 mb-4">Visitez-nous</p>
              <p className="text-sm font-semibold text-gray-700">
                1901 Pennsylvania Avenue NW<br />Washington D.C. 20006
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 lg:p-10">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Envoyez-nous un Message</h2>
                <p className="text-gray-600">Remplissez le formulaire ci-dessous et nous vous répondrons dans les plus brefs délais.</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Name */}
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                    Nom complet *
                  </label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    value={formData?.name}
                    onChange={handleInputChange}
                    placeholder="Votre nom"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all"
                    style={{focusRingColor: '#96121D'}}
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Email professionnel *
                  </label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    value={formData?.email}
                    onChange={handleInputChange}
                    placeholder="votre.email@entreprise.com"
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all"
                  />
                </div>

                {/* Phone */}
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Téléphone
                  </label>
                  <Input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData?.phone}
                    onChange={handleInputChange}
                    placeholder="+1 (240) 978-4155"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all"
                  />
                </div>

                {/* Company */}
                <div>
                  <label htmlFor="company" className="block text-sm font-semibold text-gray-700 mb-2">
                    Entreprise
                  </label>
                  <Input
                    type="text"
                    id="company"
                    name="company"
                    value={formData?.company}
                    onChange={handleInputChange}
                    placeholder="Nom de votre entreprise"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all"
                  />
                </div>

                {/* Service */}
                <div>
                  <label htmlFor="service" className="block text-sm font-semibold text-gray-700 mb-2">
                    Service concerné *
                  </label>
                  <select
                    id="service"
                    name="service"
                    value={formData?.service}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all bg-white"
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="formation">Formation professionnelle</option>
                    <option value="conseil">Conseil stratégique</option>
                    <option value="conference">Organisation de conférences</option>
                    <option value="recherche">Études et recherches</option>
                    <option value="rh">Gestion des ressources humaines</option>
                    <option value="marketing">Marketing digital</option>
                    <option value="ia">Intelligence artificielle</option>
                    <option value="projet">Management de projets numériques</option>
                    <option value="autre">Autre</option>
                  </select>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData?.message}
                    onChange={handleInputChange}
                    placeholder="Décrivez votre projet ou votre demande..."
                    required
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-opacity-50 transition-all resize-none"
                  />
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="w-full py-4 rounded-lg font-semibold text-white transition-all duration-300 hover:shadow-lg"
                  style={{backgroundColor: '#96121D'}}
                >
                  <span className="flex items-center justify-center space-x-2">
                    <span>Envoyer le message</span>
                    <Icon name="Send" size={18} />
                  </span>
                </Button>
              </form>
            </div>

            {/* Company Info & Response Times */}
            <div className="space-y-6">
              {/* Company Card */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex items-start space-x-4 mb-6">
                  <Image
                    src="/assets/images/logo_CRF_PERFECTION_4x-1761998753526.png"
                    alt="CRF PERFECTION logo featuring interlocking heart-shaped design in red and gold colors representing Centre de Recherche et de Formation business therapy excellence"
                    className="w-16 h-16 object-contain"
                  />
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">CRF PERFECTION</h3>
                    <p className="text-sm font-semibold" style={{color: '#9B8E57'}}>Centre de Recherche et de Formation</p>
                  </div>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  Cabinet international de formation, de conseil stratégique et de recherche appliquée, basé aux États-Unis, avec des agences en Afrique de l'Ouest (Bénin, Côte d'Ivoire, Togo).
                </p>
              </div>

              {/* Response Times */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Temps de Réponse Garantis</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4 p-4 rounded-xl" style={{backgroundColor: 'rgba(150, 18, 29, 0.05)'}}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'rgba(150, 18, 29, 0.1)'}}>
                      <Icon name="Zap" size={20} style={{color: '#96121D'}} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Urgence Critique</h4>
                      <p className="text-sm text-gray-600 mb-2">Crise organisationnelle majeure</p>
                      <p className="text-2xl font-bold" style={{color: '#96121D'}}>2 heures</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 rounded-xl" style={{backgroundColor: 'rgba(155, 142, 87, 0.05)'}}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'rgba(155, 142, 87, 0.1)'}}>
                      <Icon name="Clock" size={20} style={{color: '#9B8E57'}} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Consultation Standard</h4>
                      <p className="text-sm text-gray-600 mb-2">Demande d'information générale</p>
                      <p className="text-2xl font-bold" style={{color: '#9B8E57'}}>24 heures</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4 p-4 rounded-xl" style={{backgroundColor: 'rgba(150, 18, 29, 0.05)'}}>
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0" style={{backgroundColor: 'rgba(150, 18, 29, 0.1)'}}>
                      <Icon name="FileText" size={20} style={{color: '#96121D'}} />
                    </div>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">Devis Personnalisé</h4>
                      <p className="text-sm text-gray-600 mb-2">Étude de projet complexe</p>
                      <p className="text-2xl font-bold" style={{color: '#96121D'}}>48 heures</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Operating Hours */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl shadow-lg p-8 text-white">
                <h3 className="text-xl font-bold mb-6">Horaires d'Ouverture</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-gray-300">Lundi - Vendredi</span>
                    <span className="font-bold" style={{color: '#9B8E57'}}>8h00 - 19h00</span>
                  </div>
                  <div className="flex justify-between items-center pb-3 border-b border-white/10">
                    <span className="text-gray-300">Samedi</span>
                    <span className="font-bold" style={{color: '#9B8E57'}}>9h00 - 14h00</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300">Dimanche</span>
                    <span className="font-bold text-gray-400">Fermé</span>
                  </div>
                </div>
                <div className="mt-6 p-4 rounded-lg" style={{backgroundColor: 'rgba(155, 142, 87, 0.1)'}}>
                  <p className="text-sm text-gray-300">
                    <Icon name="Info" size={16} className="inline mr-2" style={{color: '#9B8E57'}} />
                    Support d'urgence disponible 24/7 pour nos clients premium
                  </p>
                </div>
              </div>

              {/* Certifications */}
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Nos Accréditations</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50">
                    <Icon name="Award" size={20} style={{color: '#96121D'}} />
                    <span className="text-sm font-semibold text-gray-700">ISO 9001</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50">
                    <Icon name="Shield" size={20} style={{color: '#9B8E57'}} />
                    <span className="text-sm font-semibold text-gray-700">Certifié QUALIOPI</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50">
                    <Icon name="Star" size={20} style={{color: '#96121D'}} />
                    <span className="text-sm font-semibold text-gray-700">5+ ans d'expertise</span>
                  </div>
                  <div className="flex items-center space-x-3 p-3 rounded-lg bg-gray-50">
                    <Icon name="CheckCircle" size={20} style={{color: '#9B8E57'}} />
                    <span className="text-sm font-semibold text-gray-700">95% satisfaction</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Notre Localisation</h2>
            <p className="text-lg text-gray-600">Visitez notre siège social à Washington D.C.</p>
          </div>
          <div className="bg-gray-200 rounded-2xl overflow-hidden shadow-lg" style={{height: '450px'}}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3105.0977326565937!2d-77.04046!3d38.8977!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89b7b7bcdecbb1df%3A0x715969d86d0b76bf!2sThe%20White%20House!5e0!3m2!1sen!2sus!4v1234567890"
              width="100%"
              height="100%"
              style={{border: 0}}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="CRF PERFECTION Location Map"
            />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-gray-900 via-[#1a0a0d] to-black relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-96 h-96 rounded-full" style={{background: 'radial-gradient(circle, #96121D 0%, transparent 70%)'}}></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 rounded-full" style={{background: 'radial-gradient(circle, #9B8E57 0%, transparent 70%)'}}></div>
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Prêt à Transformer Votre Organisation ?
          </h2>
          <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
            Nos experts sont prêts à vous accompagner dans vos projets de formation, conseil stratégique et transformation digitale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+12409784155"
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 rounded-lg font-semibold text-white transition-all duration-300 hover:shadow-lg"
              style={{backgroundColor: '#96121D'}}
            >
              <Icon name="Phone" size={20} />
              <span>Appelez-nous</span>
            </a>
            <a
              href="https://wa.me/12409784155"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center space-x-2 px-8 py-4 rounded-lg font-semibold text-gray-900 bg-white transition-all duration-300 hover:shadow-lg"
            >
              <Icon name="MessageCircle" size={20} style={{color: '#9B8E57'}} />
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;